// ./src/components/features/Logbook/LogbookEditDrawer/sections/ExpenseFieldsSection.tsx

import React, { useMemo } from "react";
import {
  NumberInput,
  Grid,
  Group,
  Text,
  TextInput,
  Box,
  SegmentedControl,
  Divider,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { FormData, isTireRelatedKindId } from "../../types";

import { CurrencySelector } from "@/components/generic/CurrencySelector";
import { ExpenseKindSelector } from "@/components/features/ExpenseKindSelector";
import { TireSetSelector } from "@/components/features/TireSets/TireSetSelector";

// ============================================
// Types
// ============================================

export interface ExpenseFieldsSectionProps {
  formData: FormData;
  onFieldChange: (field: string | undefined, value: string | number | boolean | string[] | null) => void;
  onTextInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onTotalPriceChange: (value: number | string) => void;
  onCostWorkChange: (value: number | string) => void;
  onCostPartsChange: (value: number | string) => void;
  onTaxChange: (value: number | string) => void;
  onFeesChange: (value: number | string) => void;
  onTotalPriceInHcChange: (value: number | string) => void;
  onAmountModeChange: (value: string) => void;
  onTireSetChange: (name: string, value: string | null) => void;
  breakdownTotal: number;
  isDifferentCurrency: boolean;
  apiFieldErrors: Record<string, string | undefined>;
}

// ============================================
// Component
// ============================================

const ExpenseFieldsSectionComponent: React.FC<ExpenseFieldsSectionProps> = ({
  formData,
  onFieldChange,
  onTextInputChange,
  onTotalPriceChange,
  onCostWorkChange,
  onCostPartsChange,
  onTaxChange,
  onFeesChange,
  onTotalPriceInHcChange,
  onAmountModeChange,
  onTireSetChange,
  breakdownTotal,
  isDifferentCurrency,
  apiFieldErrors,
}) => {
  const { t } = useTranslation(["expenses", "logbook", "common", "tireSets"]);

  const isTireRelated = isTireRelatedKindId(formData.kindId);

  // Amount mode options
  const amountModeOptions = useMemo(
    () => [
      { value: "total", label: t("expenses:form.amountMode.total") },
      { value: "breakdown", label: t("expenses:form.amountMode.breakdown") },
    ],
    [t]
  );

  return (
    <>
      {/* Expense Kind Selector */}
      <ExpenseKindSelector
        name="kindId"
        value={formData.kindId}
        onChange={onFieldChange}
        error={apiFieldErrors.kindId}
        required
        size="md"
      />

      {/* Tire Set Selector (conditional) */}
      {isTireRelated && (
        <TireSetSelector
          name="tireSetId"
          value={formData.tireSetId}
          onChange={onTireSetChange}
          carId={formData.carId}
          label={t("common:selectors.tireSet.label")}
          size="md"
        />
      )}

      {/* Short Note */}
      <TextInput
        label={t("expenses:form.fields.shortNote.label")}
        placeholder={t("expenses:form.fields.shortNote.placeholder")}
        name="shortNote"
        value={formData.shortNote}
        onChange={onTextInputChange}
        error={apiFieldErrors.shortNote}
        size="md"
        maxLength={100}
      />

      {/* Cost Section */}
      <Divider label={t("expenses:form.sections.cost")} labelPosition="left" />

      {/* Amount Mode Toggle */}
      <SegmentedControl
        value={formData.amountMode}
        onChange={onAmountModeChange}
        data={amountModeOptions}
        fullWidth
        size="sm"
      />

      {/* === TOTAL MODE === */}
      {formData.amountMode === "total" && (
        <Box>
          <Text size="sm" fw={500} mb={4}>
            {t("expenses:form.fields.totalPrice.label")}
          </Text>
          <Group gap="xs" align="flex-start">
            <NumberInput
              placeholder={t("expenses:form.fields.totalPrice.placeholder")}
              value={formData.totalPrice ?? ""}
              onChange={onTotalPriceChange}
              error={apiFieldErrors.totalPrice}
              min={0}
              decimalScale={2}
              size="md"
              hideControls
              style={{ flex: 1 }}
            />
            <CurrencySelector
              name="paidInCurrency"
              value={formData.paidInCurrency}
              onChange={onFieldChange}
              compact
              size="md"
            />
          </Group>
          {isDifferentCurrency && formData.totalPriceInHc != null && (
            <Text size="xs" c="dimmed" mt={4}>
              ≈ {formData.totalPriceInHc.toFixed(2)} {formData.homeCurrency}
            </Text>
          )}
        </Box>
      )}

      {/* === BREAKDOWN MODE === */}
      {formData.amountMode === "breakdown" && (
        <>
          {/* Work Cost & Parts Cost */}
          <Grid gutter="sm">
            <Grid.Col span={{ base: 6 }}>
              <NumberInput
                label={t("expenses:form.fields.costWork.label")}
                placeholder={t("expenses:form.fields.costWork.placeholder")}
                value={formData.costWork ?? ""}
                onChange={onCostWorkChange}
                error={apiFieldErrors.costWork}
                min={0}
                decimalScale={2}
                size="md"
                hideControls
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6 }}>
              <NumberInput
                label={t("expenses:form.fields.costParts.label")}
                placeholder={t("expenses:form.fields.costParts.placeholder")}
                value={formData.costParts ?? ""}
                onChange={onCostPartsChange}
                error={apiFieldErrors.costParts}
                min={0}
                decimalScale={2}
                size="md"
                hideControls
              />
            </Grid.Col>
          </Grid>

          {/* Tax & Fees */}
          <Grid gutter="sm">
            <Grid.Col span={{ base: 6 }}>
              <NumberInput
                label={t("expenses:form.fields.tax.label")}
                placeholder={t("expenses:form.fields.tax.placeholder")}
                value={formData.tax ?? ""}
                onChange={onTaxChange}
                error={apiFieldErrors.tax}
                min={0}
                decimalScale={2}
                size="md"
                hideControls
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6 }}>
              <NumberInput
                label={t("expenses:form.fields.fees.label")}
                placeholder={t("expenses:form.fields.fees.placeholder")}
                value={formData.fees ?? ""}
                onChange={onFeesChange}
                error={apiFieldErrors.fees}
                min={0}
                decimalScale={2}
                size="md"
                hideControls
              />
            </Grid.Col>
          </Grid>

          {/* Auto-calculated Total + Currency */}
          <Box>
            <Group justify="space-between" align="flex-end">
              <Group gap="xs" align="baseline">
                <Text size="sm" c="dimmed">
                  {t("expenses:form.fields.totalPrice.label")}:
                </Text>
                <Text size="lg" fw={600}>
                  {breakdownTotal > 0
                    ? breakdownTotal.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    : "0.00"}
                </Text>
              </Group>
              <CurrencySelector
                name="paidInCurrency"
                value={formData.paidInCurrency}
                onChange={onFieldChange}
                compact
                size="md"
              />
            </Group>
            {isDifferentCurrency && formData.totalPriceInHc != null && (
              <Text size="xs" c="dimmed" mt={4}>
                ≈ {formData.totalPriceInHc.toFixed(2)} {formData.homeCurrency}
              </Text>
            )}
          </Box>
        </>
      )}

      {/* Home Currency Conversion (both modes) */}
      {isDifferentCurrency && (
        <NumberInput
          label={t("expenses:form.fields.totalPriceInHc.label")}
          placeholder={t("expenses:form.fields.totalPriceInHc.placeholder")}
          description={`${t("expenses:form.fields.homeCurrency.label")}: ${formData.homeCurrency}`}
          value={formData.totalPriceInHc ?? ""}
          onChange={onTotalPriceInHcChange}
          min={0}
          decimalScale={2}
          size="md"
          hideControls
        />
      )}
    </>
  );
};

export const ExpenseFieldsSection = React.memo(ExpenseFieldsSectionComponent);